// COLOUR LOOK (kept until the owner retires it; the one-bit reactions are in reactions.js).
// Wrong-choice reaction mock-ups. Each stage draws the real scene (scene.js) and visitor (visitors.js),
// then layers its own beat on top. Everything is a pure function of the stage clock t (ms), so a frame
// can be reproduced exactly: ?t=3000 freezes every stage at 3 s (handy for screenshots).
import { createScene, W, H } from './scene.js';
import { makeLook, drawVisitor, seedOf } from './visitors.js';

const P = { woodD: '#2e1c12', wood: '#5e3a22', woodL: '#8a5a32', sod: '#f0a640', glow: '#ffd98a', paper: '#e9dcb8',
  pink: '#d0607a', metal: '#3b4447', metalL: '#5d6a6b', black: '#05060b', houseOff: '#2a3438' };
const SICK = ['#9fb08a', '#6f7d5c', '#4e5a40'];
const BAYER = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]];
const LIGHT = {
  warm: { glow: P.glow, sod: P.sod, mid: '#5a4a35', bulb: P.glow, floor: [P.wood, P.woodL] },
  sick: { glow: '#c8dc8a', sod: '#8fae4a', mid: '#3e4a2a', bulb: '#c8dc8a', floor: [SICK[2], SICK[1]] },
  off: { bulb: '#2a2a24' },
};
const DOOR = { x: 124, y: 20, w: 152, h: 176 }; // the door leaf inside its frame
const LENS = { x: 200, y: 96, r: 62 };

// ---------- small helpers ----------
const clamp01 = x => Math.max(0, Math.min(1, x));
const seg = (t, a, b) => clamp01((t - a) / (b - a));
const easeOut = x => 1 - (1 - x) ** 3;
const easeIn = x => x * x * x;
const easeInOut = x => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2);
const easeOutBack = x => 1 + 2.70158 * (x - 1) ** 3 + 1.70158 * (x - 1) ** 2;
const lerp = (a, b, u) => a + (b - a) * u;
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const hash = (...n) => rng(n.reduce((h, v) => Math.imul(h ^ v, 2654435761) >>> 0, 0x9e3779b9))();
function mk(w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d');
  const R = (x, y, ww, hh, col) => { g.fillStyle = col; g.fillRect(x, y, ww, hh); };
  return [c, g, R];
}
function findLook(prefix, ok, opts) {
  for (let i = 0; i < 500; i++) { const l = makeLook(seedOf(prefix + i), opts); if (ok(l)) return l; }
  return makeLook(seedOf(prefix), opts);
}

// ---------- visitor sprites: drawVisitor rendered once into a canvas, then scaled with nearest-neighbour ----------
const SW = 72, SH = 150, AX = 36, AY = 28, T_OPEN = 10, T_BLINK = 141;
const fyOf = look => AY + 8 + 1 + look.height; // the visitor's face line (mouth row) at T_OPEN/T_BLINK
const feetOf = look => fyOf(look) + 94;
const spriteCache = new Map();
function sprite(look, reveal, { blink = false, back = false, legs = null } = {}) {
  const key = [seedOf(JSON.stringify(look)), reveal, blink, back, legs].join('|');
  if (spriteCache.has(key)) return spriteCache.get(key);
  const [c, , R] = mk(SW, SH);
  if (legs !== null) drawLegs(R, look, legs);
  drawVisitor(R, AX, AY, look, blink ? T_BLINK : T_OPEN, { reveal });
  if (back) drawBackOfHead(R, look);
  spriteCache.set(key, c);
  return c;
}
function drawLegs(R, look, phase) {
  const top = fyOf(look) + 70, a = phase ? 3 : 0, b = phase ? 0 : 3;
  R(AX - 9, top, 7, 20 - a, '#2a2833'); R(AX - 10, top + 20 - a, 9, 3, '#0e0d12');
  R(AX + 2, top, 7, 20 - b, '#2a2833'); R(AX + 2, top + 20 - b, 9, 3, '#0e0d12');
}
function drawBackOfHead(R, look) {
  const hcy = fyOf(look) - 6, hw = look.headW, hh = look.headH;
  R(AX - hw - 1, hcy - 1, 2, 4, look.skin[1]); R(AX + hw - 1, hcy - 1, 2, 4, look.skin[2]);
  for (let dy = -hh - 1; dy <= hh - 2; dy++) {
    const w = Math.round((hw + 1) * Math.sqrt(Math.max(0, 1 - (dy / (hh + 1)) ** 2)));
    if (!w) continue;
    R(AX - w, hcy + dy, w * 2, 1, look.hairColor);
    R(AX + Math.round(w * 0.4), hcy + dy, w - Math.round(w * 0.4), 1, '#00000033');
  }
  R(AX - 3, hcy + hh - 2, 6, 3, look.skin[1]);
}
// Only the alien's pink pinpricks, with a one-pixel ember around them.
function eyesOf(src) {
  const [c, g] = mk(SW, SH);
  const d = src.getContext('2d').getImageData(0, 0, SW, SH).data, pts = [];
  for (let i = 0; i < SW * SH; i++) if (d[i * 4] === 208 && d[i * 4 + 1] === 96 && d[i * 4 + 2] === 122 && d[i * 4 + 3] === 255) pts.push([i % SW, Math.floor(i / SW)]);
  g.fillStyle = '#4a1f2c'; for (const [x, y] of pts) g.fillRect(x - 1, y - 1, 3, 3);
  g.fillStyle = P.pink; for (const [x, y] of pts) g.fillRect(x, y, 1, 1);
  return c;
}
// Draw a sprite with sprite point (AX, py) at screen (ax, ay), scale s. The head (rows above `neck`)
// can be stretched upward by k, and `tear(band)` can shove bands sideways for a glitch.
function blit(ctx, spr, ax, ay, s, { py = AY, k = 1, neck = 0, tear = null } = {}) {
  if (k === 1 && !tear) {
    ctx.drawImage(spr, 0, 0, SW, SH, Math.round(ax - AX * s), Math.round(ay - py * s), Math.round(SW * s), Math.round(SH * s));
    return;
  }
  const x0 = Math.round(ax - AX * s), ny = ay + (neck - py) * s;
  for (let r = 0, band = 0; r < SH; band++) {
    const h = Math.min(3, SH - r), off = tear ? tear(band) : 0;
    const f = row => (row <= neck ? ny - (neck - row) * s * k : ny + (row - neck) * s);
    const y0 = Math.round(f(r)), y1 = Math.round(f(r + h));
    if (y1 > y0) ctx.drawImage(spr, 0, r, SW, h, x0 + off, y0, Math.round(SW * s), y1 - y0);
    r += h;
  }
}

// ---------- the open doorway: outside, porch lamp, floor light ----------
const bgCache = {};
function outside(kind) {
  if (bgCache['o' + kind]) return bgCache['o' + kind];
  const [c, , R] = mk(DOOR.w, DOOR.h), L = LIGHT[kind];
  const roof = i => 34 + Math.floor(hash(i, 3) * 26);
  for (let y = 0; y < DOOR.h; y++) for (let x = 0; x < DOOR.w; x++) {
    const px = x + DOOR.x, py = y + DOOR.y, b = BAYER[py & 3][px & 3];
    let base;
    if (y > 132) base = (Math.floor(px / 6) + Math.floor(py / 4)) % 2 ? '#2b2a24' : '#33302a';
    else if (y >= roof(Math.floor(x / 14))) base = x % 7 < 2 && y % 9 < 2 && y < 120 && hash(Math.floor(x / 7), Math.floor(y / 9), 9) < 0.18 ? '#8a6a3a' : '#141a26';
    else base = '#0b0f1c';
    let col = base;
    if (kind !== 'off') {
      const lamp = Math.max(0, 1 - Math.hypot((x - 76) / 66, (y - 4) / 150));
      col = lamp * 16 > b + 2 ? (lamp > 0.6 ? L.glow : L.sod) : lamp * 24 > b ? L.mid : base;
    }
    R(x, y, 1, 1, col);
  }
  return (bgCache['o' + kind] = c);
}
function floorLight(kind) {
  if (bgCache['f' + kind]) return bgCache['f' + kind];
  const [c, , R] = mk(W, 33), L = LIGHT[kind];
  for (let j = 0; j < 33; j++) {
    const y = 192 + j, inDoor = j < 4, half = inDoor ? 76 : 76 + (j - 4) * 2.6;
    for (let x = Math.floor(200 - half); x < 200 + half; x++) {
      const b = BAYER[y & 3][x & 3], edge = clamp01((1 - Math.abs(x + 0.5 - 200) / half) * 3);
      const inten = (inDoor ? 1 : 1 - (j - 4) / 36) * edge;
      if (inten > 0.55 && inten * 16 > b + 6) R(x, j, 1, 1, L.floor[1]);
      else if (inten * 16 > b) R(x, j, 1, 1, L.floor[0]);
    }
  }
  return (bgCache['f' + kind] = c);
}
function drawDoorway(S, t, kind, sick) {
  const { ctx, R } = S;
  ctx.drawImage(outside(kind), DOOR.x, DOOR.y);
  R(194, 20, 12, 3, P.metal); R(197, 23, 6, 3, LIGHT[kind].bulb);
  for (let i = 0; i < 28; i++) {
    const sp = 0.12 + (i % 3) * 0.04, x = DOOR.x + ((i * 53 + Math.floor(t * 0.02)) % DOOR.w);
    const y = DOOR.y + ((i * 37 + Math.floor(t * sp)) % DOOR.h);
    R(x, y, 1, sick ? 1 : 3, sick ? '#7f9a5a' : '#55617a');
  }
}
function drawFloorLight(S, kind, u) {
  if (kind === 'off' || u <= 0) return;
  const rows = Math.round(33 * u);
  S.ctx.drawImage(floorLight(kind), 0, 0, W, rows, 0, 192, W, rows);
}
function drawShadow(S, cx, u) {
  for (let j = 0; j < Math.round(29 * u); j++) {
    const y = 196 + j, half = Math.round(11 + j * 1.1);
    for (let x = cx - half; x < cx + half; x++) if ((x + y) & 1 || j < 6) S.R(x, y, 1, 1, P.woodD);
  }
}
// The door leaf swings toward you on its left hinge: narrower, and taller at the free edge.
function drawDoorLeaf(S, ang) {
  const { ctx, R, snap } = S;
  const sn = Math.sin(ang), w = Math.max(2, Math.round(DOOR.w * Math.cos(ang)));
  ctx.fillStyle = P.black;
  for (let i = 0; i < w; i++) {
    const sx = Math.min(DOOR.w - 1, Math.floor((i * DOOR.w) / w)), grow = Math.round(20 * sn * (i / w));
    ctx.drawImage(snap, sx, 0, 1, DOOR.h, DOOR.x + i, DOOR.y - grow, 1, DOOR.h + grow * 2);
    ctx.globalAlpha = 0.6 * sn; ctx.fillRect(DOOR.x + i, DOOR.y - grow, 1, DOOR.h + grow * 2); ctx.globalAlpha = 1;
  }
  const g = Math.round(20 * sn), ew = Math.round(5 * sn);
  if (ew) { R(DOOR.x + w, DOOR.y - g, ew, DOOR.h + g * 2, P.woodD); R(DOOR.x + w, DOOR.y - g, 1, DOOR.h + g * 2, P.wood); }
}
function drawHouse(R, i, c) { const x = 34 + i * 8; R(x, 7, 6, 4, c); R(x + 1, 5, 4, 2, c); R(x + 2, 4, 2, 1, c); }
function houseColour(t, a) { // house i: lit, flickers sick, holds sick, dies
  if (t < a) return P.sod;
  if (t < a + 450) return hash(Math.floor(t / 70), 5) < 0.5 ? P.sod : SICK[0];
  if (t < a + 800) return SICK[0];
  if (t < a + 1000) return SICK[2];
  return P.houseOff;
}

// ---------- reaction 1: infected ----------
const sickLook = findLook('outbreak-', l => !l.mask && ['short', 'curly', 'bob', 'bun', 'long'].includes(l.hairStyle) && !l.glasses, { outbreak: true });
const SPORES = (() => {
  const r = rng(71);
  return Array.from({ length: 110 }, (_, i) => ({
    born: (i < 60 ? 2500 : 2780) + r() * 140, ang: r() * Math.PI * 2, v: 18 + r() ** 0.7 * 150,
    dx: (r() - 0.5) * 18, dy: -1 - r() * 5, ph: r() * 6.3, c: SICK[Math.floor(r() * 3)], s: r() < 0.28 ? 2 : 1,
  }));
})();
const infected = {
  duration: 8000, outbreak: true, key: 'open', keyAt: 250, revealAt: 5900, darkAt: Infinity,
  bulletin: 'Visitors never ask you who, why or how.',
  chat: [[0, 'sys', 'Knock knock. Rui says they live in 4B.'], [0, 'me', 'how is it out there?'], [0, 'them', "just a cold, i swear. please, it's freezing"]],
  board: [['mara', 1240], ['kofi', 980]], you: [420, 260],
  reveal: ['You let the infection in. −160 (double)', 'Flat 4B is sick.'],
  shake(t) {
    if (t >= 600 && t < 760) return 3;
    if (t >= 1020 && t < 1120) return 2;
    if (t >= 2100 && t < 2240) return 3;
    if ((t >= 2500 && t < 2580) || (t >= 2780 && t < 2860)) return 1;
    if (t >= 5900 && t < 6020) return 2;
    return 0;
  },
  render(S, t) {
    const L = S.look ?? sickLook, hi = S.house ?? 6;
    const { ctx, R } = S;
    const open = t >= 600;
    S.scene({ visitor: open ? null : L, reveal: null, outbreak: S.outbreak ?? true, timerFrac: t < 250 ? 0.27 : 0, shelter: S.shelter ?? { left: 7, total: 10 } });
    if (open) {
      S.grabDoor();
      let kind = 'warm';
      if (t >= 3700 && t < 4600) { const u = hash(Math.floor(t / 80), 11); kind = u < 0.3 ? 'off' : u < 0.3 + seg(t, 3700, 4600) * 0.7 ? 'sick' : 'warm'; }
      else if (t >= 4600) kind = 'sick';
      drawDoorway(S, t, kind, true);
      const spr = sprite(L, 'infected');
      if (t < 2100) { // standing on the step, rain behind them
        ctx.save(); ctx.beginPath(); ctx.rect(DOOR.x, DOOR.y, DOOR.w, DOOR.h); ctx.clip();
        blit(ctx, spr, 200, 86 + (Math.floor(t / 500) % 2), 2);
        ctx.restore();
      }
      drawDoorLeaf(S, (Math.PI / 180) * 78 * easeOutBack(seg(t, 600, 1050)));
      drawFloorLight(S, kind, easeOut(seg(t, 700, 1150)));
      const inRoom = t >= 2100 && t < 3900;
      if (t < 3400) drawShadow(S, 200 - Math.round(easeIn(seg(t, 3200, 3400)) * 60), easeOut(seg(t, 700, 1150)) * (t >= 2100 ? 1.3 : 1));
      // a sudden step inside: cough, cough, then past you into the flat
      const hunch = (t >= 2480 && t < 2640) || (t >= 2760 && t < 2920) ? 3 : 0;
      const vx = 200 - Math.round(easeIn(seg(t, 3200, 3900)) * 300), vy = 74 + hunch + (t >= 3200 ? Math.floor(t / 160) % 2 : 0);
      if (inRoom) blit(ctx, spr, vx, vy, 3);
      const mx = 203, my = 74 + (fyOf(L) - AY) * 3 + 3;
      for (const born of [2500, 2780]) { // the cough itself: a dithered green puff
        const a = seg(t, born, born + 650);
        if (a <= 0 || a >= 1) continue;
        const rad = Math.round(5 + a * 20), dens = (1 - a) * 12;
        for (let y = -rad; y <= rad; y++) for (let x = -rad; x <= rad; x++) {
          const d = Math.hypot(x, y * 1.4) / rad, px = mx + x, py = my + y;
          if (d <= 1 && (1 - d) * dens > BAYER[py & 3][px & 3]) R(px, py, 1, 1, d < 0.45 ? SICK[0] : SICK[1]);
        }
      }
      for (const p of SPORES) {
        const a = (t - p.born) / 1000;
        if (a < 0) continue;
        const rr = p.v * (1 - Math.exp(-a / 0.5));
        const x = Math.round(mx + Math.cos(p.ang) * rr + Math.sin(a * 1.6 + p.ph) * 3 + p.dx * a);
        const y = Math.round(my + Math.sin(p.ang) * rr * 0.62 + p.dy * a + Math.cos(a * 1.1 + p.ph) * 2);
        R(x, y, p.s, p.s, p.c);
      }
    }
    drawHouse(R, hi, houseColour(t, 4800));
  },
};

// ---------- reaction 2: AI ----------
const aiLook = { ...findLook('ai-', l => l.alienFace === 'wells' && ['short', 'bob', 'bun', 'curly', 'ponytail', 'long'].includes(l.hairStyle) && !l.mask), expression: 'smile' };
const alien = {
  duration: 8000, outbreak: false, key: 'open', keyAt: 250, revealAt: 5800, darkAt: 3800,
  bulletin: 'Visitors never say day, night, week, today, tonight, morning or a weekday name.',
  chat: [[0, 'sys', 'Knock knock. Noor says they live in 2A.'], [0, 'me', 'what day is it?'], [0, 'them', 'a good moment to come inside.']],
  board: [['mara', 1240], ['kofi', 980]], you: [420, 340],
  reveal: ["It wasn't human. −100", 'One of the block\'s homes goes dark.'],
  shake(t) {
    if (t >= 600 && t < 760) return 3;
    if (t >= 1020 && t < 1120) return 2;
    if (t >= 2200 && t < 3000 && hash(Math.floor(t / 60), 2) < 0.35) return 1;
    if (t >= 3800 && t < 3960) return 3;
    if (t >= 5800 && t < 5920) return 2;
    return 0;
  },
  render(S, t) {
    const L = S.look ?? aiLook, hi = S.house ?? 6, total = S.shelter?.total ?? 10;
    const { ctx, R } = S;
    const open = t >= 600, human = sprite(L, null), it = sprite(L, 'alien');
    const neck = fyOf(L) + 2, ay = 90;
    if (t >= 3800) { // the lights are gone; only what it sees with is left
      R(0, 0, W, H, P.black);
      for (let i = 0; i < total; i++) drawHouse(R, i, i < hi ? P.sod : i === hi ? (t < 4900 ? P.sod : t < 5500 ? (hash(Math.floor(t / 70), 4) < 0.55 ? P.sod : P.houseOff) : P.houseOff) : P.houseOff);
      const blinkOff = t >= 4500 && t < 4620;
      // in the dark it keeps rising, until the eyes sit above where a head could be
      const ny = ay + (neck - AY) * 2, kTop = (ny - 38) / ((neck - (fyOf(L) - 7)) * 2);
      const ey = fyOf(L) - 7, k = lerp(1.45, kTop, easeInOut(seg(t, 3900, 5300)));
      if (!blinkOff) blit(ctx, S.cache('eyes', () => eyesOf(it)), 200, Math.round(ny - (neck - ey) * 2 * k), 2, { py: ey });
      return;
    }
    S.scene({ visitor: open ? null : L, reveal: null, outbreak: S.outbreak ?? false, timerFrac: t < 250 ? 0.31 : 0, shelter: S.shelter ?? { left: 7, total: 10 } });
    if (!open) return;
    S.grabDoor();
    const glitch = t >= 2200 && t < 3000, step = Math.floor(t / 60);
    const kind = glitch && hash(step, 8) < 0.3 ? 'off' : 'warm';
    drawDoorway(S, t, kind, false);
    ctx.save(); ctx.beginPath(); ctx.rect(DOOR.x, DOOR.y - 20, DOOR.w, DOOR.h + 20); ctx.clip();
    if (t < 2200) blit(ctx, human, 200, ay + (Math.floor(t / 600) % 2), 2);
    else if (glitch) {
      const p = seg(t, 2200, 2900), showAlien = hash(step, 1) < 0.2 + p * 0.8;
      blit(ctx, showAlien ? it : human, 200, ay, 2, { neck, tear: band => (hash(step, band, 3) < 0.25 + p * 0.3 ? Math.round((hash(step, band, 4) - 0.5) * 16) : 0) });
      if (hash(step, 6) < 0.5) R(160, ay - 20 + Math.floor(hash(step, 7) * 60), 80, 1, P.pink);
    } else {
      const k = lerp(1, 1.45, easeIn(seg(t, 3000, 3700)));
      blit(ctx, it, 200, ay, 2, { k, neck, tear: band => (hash(step, band, 5) < 0.08 ? Math.round((hash(step, band, 4) - 0.5) * 10) : 0) });
    }
    ctx.restore();
    drawDoorLeaf(S, (Math.PI / 180) * 78 * easeOutBack(seg(t, 600, 1050)));
    drawFloorLight(S, kind, easeOut(seg(t, 700, 1150)));
    drawShadow(S, 200, easeOut(seg(t, 700, 1150)));
    if (glitch) for (let i = 0; i < 3; i++) if (hash(step, i, 9) < 0.5) { // the whole picture tears
      const y = Math.floor(hash(step, i, 10) * 200), h = 2 + Math.floor(hash(step, i, 11) * 8);
      ctx.drawImage(S.canvas, 0, y, W, h, Math.round((hash(step, i, 12) - 0.5) * 12), y, W, h);
    }
    drawHouse(R, hi, P.sod);
  },
};

// ---------- reaction 3: a human, kept out ----------
const kindLook = { ...findLook('kept-', l => ['short', 'curly', 'bob', 'long', 'ponytail'].includes(l.hairStyle) && !l.mask && !l.beard && !l.glasses), expression: 'worried' };
const human = {
  duration: 8000, outbreak: false, key: 'shut', keyAt: 200, revealAt: 5600, darkAt: Infinity,
  bulletin: 'Visitors never name a colour, like red or blue.',
  chat: [[0, 'sys', 'Knock knock. Ada says they live in 3C.'], [0, 'me', 'what colour is your door?'], [0, 'them', 'blue. it was always blue.'],
    [1550, 'sys', 'A weak knock.'], [3300, 'them', "fine. i'll find somewhere else."]],
  board: [['mara', 1240], ['kofi', 980]], you: [420, 340],
  reveal: ['They were human. −100', 'You left a neighbour in the cold.'],
  shake(t) {
    if (t >= 1550 && t < 1640) return 1;
    if (t >= 5600 && t < 5720) return 2;
    return 0;
  },
  render(S, t) {
    const L = S.look ?? kindLook;
    const { ctx, R } = S;
    S.scene({ visitor: null, reveal: null, outbreak: S.outbreak ?? false, timerFrac: t < 200 ? 0.22 : 0, shelter: S.shelter ?? { left: 7, total: 10 }, flicker: hash(Math.floor(t / 140), 13) < 0.06 ? 0.45 : 1 });
    ctx.save(); ctx.beginPath(); ctx.arc(LENS.x, LENS.y, LENS.r * 0.8, 0, 7); ctx.clip();
    const fy = fyOf(L), feet = feetOf(L);
    const walk = seg(t, 2600, 5000), s = 1 / (1 + 2.6 * walk);
    const footStart = LENS.y + (feet - AY), horizon = 116;
    const x = 200 + Math.round(easeIn(walk) * 66), stepping = walk > 0 && walk < 1 ? Math.floor(t / 230) % 2 : 0;
    const fyScreen = horizon + (footStart - horizon) * s - stepping + (t >= 600 && walk === 0 ? 1 : 0);
    const back = t >= 2500, blink = (t >= 1000 && t < 1080) || (t >= 2100 && t < 2180) || (t >= 2400 && t < 2500);
    if (x < 262) blit(ctx, sprite(L, null, { blink, back, legs: stepping }), x + (t >= 2400 && t < 2500 ? 1 : 0), fyScreen, s, { py: feet });
    // breath, caught by the porch light
    for (let b = 0; b < 7; b++) {
      const born = 200 + b * 620, a = (t - born) / 900;
      if (a < 0 || a > 1) continue;
      const w0 = seg(born, 2600, 5000), s0 = 1 / (1 + 2.6 * w0), x0 = 200 + Math.round(easeIn(w0) * 66);
      const mouthY = horizon + (footStart - horizon) * s0 - (feet - fy) * s0;
      const bx = Math.round(x0 + (born >= 2500 ? -6 : 1) * s0 - a * 5), by = Math.round(mouthY - 1 - a * 9 * s0);
      const sz = Math.max(1, Math.round((2 + a * 2) * s0));
      R(bx, by, sz + 1, sz, a < 0.45 ? P.paper : '#8f8a7a');
      if (a < 0.7 && s0 > 0.6) R(bx - 2, by + 2, 2, 1, '#8f8a7a');
    }
    // one more knock: the fist comes up to the door, barely
    const k = t < 1550 ? seg(t, 1250, 1550) : 1 - seg(t, 1600, 1900);
    if (k > 0) {
      const fx = 176 + Math.round(k * 6), fyy = Math.round(160 - easeOut(k) * 34), z = Math.round(8 + k * 4);
      R(fx - z / 2 - 2, fyy + z - 3, z + 4, 40, L.coat);
      R(fx - z / 2, fyy, z, z - 2, L.skin[0]); R(fx + 1, fyy, z / 2, z - 2, L.skin[1]);
      for (let i = 1; i < 3; i++) R(fx - z / 2 + Math.round((i * z) / 3), fyy + 1, 1, 3, L.skin[2]);
    }
    ctx.restore();
    if (!S.look) drawHouse(R, 6, P.sod);
  },
};

// ---------- stage runtime ----------
const REACTIONS = { infected, alien, human };
const reduce = matchMedia('(prefers-reduced-motion: reduce)');
const frozen = new URLSearchParams(location.search).get('t');

function overlays(def) {
  const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  return `<div class="shake${def.outbreak ? ' outbreak' : ''}">
    <canvas width="${W}" height="${H}"></canvas>
    <div class="ov clock">23:47</div>
    <div class="ov note"><b>Tonight's rule</b><span>${esc(def.bulletin)}</span></div>
    <div class="ov board">${def.board.map(([n, p]) => `<div><span>${n}</span><span>${p.toLocaleString('en')}</span></div>`).join('')}<div class="you"><span>you</span><span class="pts">${def.you[0]}</span></div></div>
    <div class="ov chat" aria-hidden="true">${def.chat.map(([at, cls, text]) => `<p class="${cls}" data-at="${at}">${esc(text)}</p>`).join('')}</div>
    <div class="ov ask">talk through the door</div>
    <div class="keys"><span class="key open">Open [A]</span><span class="key shut">Keep shut [D]</span></div>
    <div class="reveal" role="status"><div class="t">${esc(def.reveal[0])}</div><div class="s"><span>${esc(def.reveal[1])}</span></div><span class="btn">Next knock</span></div>
  </div>`;
}

function mount(section) {
  const def = REACTIONS[section.dataset.reaction], frame = section.querySelector('.frame');
  frame.innerHTML = `<div class="stage">${overlays(def)}</div>`;
  const stage = frame.querySelector('.stage'), shake = stage.querySelector('.shake'), canvas = stage.querySelector('canvas');
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  const scene = createScene(ctx);
  const [snapC, snapG] = mk(DOOR.w, DOOR.h);
  const local = {};
  const S = {
    ctx, canvas, snap: snapC,
    R: (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); },
    scene: st => scene.draw({ role: 'doorkeeper', flicker: 1, ...st }),
    grabDoor: () => { snapG.clearRect(0, 0, DOOR.w, DOOR.h); snapG.drawImage(canvas, DOOR.x, DOOR.y, DOOR.w, DOOR.h, 0, 0, DOOR.w, DOOR.h); },
    cache: (k, f) => local[k] ?? (local[k] = f()),
  };
  const el = {
    open: stage.querySelector('.key.open'), shut: stage.querySelector('.key.shut'), reveal: stage.querySelector('.reveal'),
    sub: stage.querySelector('.reveal .s span'), pts: stage.querySelector('.you .pts'), you: stage.querySelector('.you'),
    lines: [...stage.querySelectorAll('.chat p')],
  };
  const pressed = el[def.key], other = def.key === 'open' ? el.shut : el.open;

  function fit() {
    const w = frame.clientWidth, s = w >= W ? Math.floor(w / W) : w / W;
    stage.style.transform = `scale(${s})`;
    frame.style.height = `${Math.round(H * s)}px`;
    frame.style.width = `${Math.round(W * s)}px`;
    frame.style.width = '';
    stage.style.left = `${Math.floor((w - W * s) / 2)}px`;
  }
  new ResizeObserver(fit).observe(frame); fit();

  function draw(t, motion) {
    def.render(S, t);
    const amp = motion ? def.shake(t) : 0, step = Math.floor(t / 33);
    const dx = amp ? Math.round((hash(step, 21) - 0.5) * 2 * amp) : 0, dy = amp ? Math.round((hash(step, 22) - 0.5) * 2 * amp) : 0;
    shake.style.transform = dx || dy ? `translate(${dx}px,${dy}px)` : '';
    shake.classList.toggle('dark', t >= def.darkAt);
    pressed.classList.toggle('down', t >= def.keyAt && t < def.keyAt + 300);
    other.classList.toggle('off', t >= def.keyAt);
    pressed.classList.toggle('off', t >= def.keyAt + 300);
    for (const p of el.lines) p.hidden = t < Number(p.dataset.at);
    const shown = t >= def.revealAt;
    el.reveal.classList.toggle('on', shown);
    el.sub.style.visibility = t >= def.revealAt + 300 ? 'visible' : 'hidden';
    el.pts.textContent = shown ? def.you[1] : def.you[0];
    el.you.classList.toggle('hit', shown);
  }

  let start = performance.now(), visible = true, playing = !reduce.matches;
  const motion = () => !reduce.matches;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(frame);
  function tick(now) {
    if (frozen !== null) draw(Number(frozen), false);
    else if (playing && visible) {
      let t = now - start;
      if (reduce.matches) { if (t >= def.duration) { t = def.duration - 1; playing = false; } }
      else t %= def.duration;
      draw(t, motion());
    }
    requestAnimationFrame(tick);
  }
  if (!playing) draw(def.duration - 1, false);
  section.querySelector('.replay').addEventListener('click', () => { start = performance.now(); playing = true; });
  reduce.addEventListener('change', () => { playing = !reduce.matches; if (!playing) draw(def.duration - 1, false); else start = performance.now(); });
  requestAnimationFrame(tick);
}

document.fonts.ready.then(() => document.querySelectorAll('section[data-reaction]').forEach(mount));

// ---------- in-game player ----------
// Plays a wrong-choice reaction on the game's own canvas, with the real visitor.
// kind: 'infected' | 'alien' | 'human'. Hooks: line(cls, text) for timed chat lines, shake(dx, dy), done().
export function createReactionPlayer(ctx, scene) {
  const canvas = ctx.canvas;
  ctx.imageSmoothingEnabled = false;
  const [snapC, snapG] = mk(DOOR.w, DOOR.h);
  let run = null;
  return {
    get active() { return !!run; },
    // ahead: start that many ms into the reaction (the demo's &rt=, for screenshots)
    play(kind, { look, outbreak, shelter, house, ahead = 0 }, hooks) {
      const def = REACTIONS[kind], local = {};
      const S = {
        ctx, canvas, snap: snapC, look, outbreak, shelter, house,
        R: (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); },
        scene: st => scene.draw({ role: 'doorkeeper', flicker: 1, coins: 0, ...st }),
        grabDoor: () => { snapG.clearRect(0, 0, DOOR.w, DOOR.h); snapG.drawImage(canvas, DOOR.x, DOOR.y, DOOR.w, DOOR.h, 0, 0, DOOR.w, DOOR.h); },
        cache: (k, f) => local[k] ?? (local[k] = f()),
      };
      const lines = def.chat.filter(([at]) => at > 0).map(([at, cls, text]) => ({ at, cls, text, sent: false }));
      run = { def, S, start: performance.now() - ahead, lines, hooks };
    },
    draw(now) {
      if (!run) return;
      const { def, S, hooks } = run, t = now - run.start;
      if (t >= def.revealAt) { const h = hooks; run = null; hooks.shake?.(0, 0); h.done?.(); return; }
      def.render(S, t);
      for (const l of run.lines) if (!l.sent && t >= l.at) { l.sent = true; hooks.line?.(l.cls, l.text); }
      const amp = def.shake(t), step = Math.floor(t / 33);
      hooks.shake?.(amp ? Math.round((hash(step, 21) - 0.5) * 2 * amp) : 0, amp ? Math.round((hash(step, 22) - 0.5) * 2 * amp) : 0);
    },
    stop() { run = null; },
  };
}
